import { getAllEnumValues } from '@upright/common/utils';
import type { SupplierRiskLevel, SupplierRiskSnapshot } from '../../mcp-payloads/supplier-risk';
import { AccessLevel } from '../access/types';
import { SupplierAllowance } from './supplier-allowance';
import { OwnCompanyMatchDto } from './supplier-own-company';
import { ModelEntityReference, PossiblyNamedModelEntityReference } from '../../model/input/types';
import { MeasurableGroupType } from '../../model/input/measurable-group/types';
import {
  DueDiligenceCompanyInfo,
  EntityInfo,
  MyCompanyGroupInfo,
  MyCompanyInfo,
  MyPortfolioInfo,
} from '../entity-info-types';
import { UserGroupPlain } from '../user-groups/types';
import { UserPlain } from '../users/types';
import { Role } from '../user-groups/utils';
import { GPTProductSetGenerationTrigger } from '../../internal/jobs/types';

export enum SpecialOrganizationId {
  UPRIGHT = 1,
}

export interface Organization {
  id: number;
  type: OrganizationType;
  name: string;
  description?: string;
  companyId?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
  entities?: OrganizationEntityReference[];
  userGroup?: UserGroupPlain;
  createdBy?: UserPlain;
  ssoProperties?: OrganizationSsoProperties | null;
  /**
   * Lifecycle status; see `OrganizationLabel`. Optional in the type only because some fetches load a
   * subset of columns (e.g. a user's own organizations); every full read carries it.
   */
  label?: OrganizationLabel;
  /** Planned end of a trial as a plain `YYYY-MM-DD` date, or null when none is set. */
  trialEndsAt?: string | null;
  /** When, by whom and through which path the label was last set. Null on rows the migration backfilled. */
  labelChangedAt?: Date | null;
  labelChangedBy?: UserPlain | null;
  labelChangeSource?: OrganizationLabelChangeSource | null;
  /**
   * How many accounts outside Upright are in the organization or its teams. In a team user's read of
   * one organization only, where it backs the warning about moving the label away from Active.
   */
  externalUserCount?: number;
}

/**
 * Lifecycle status of an organization (PLT-6676): an active customer, a prospect on trial, churned,
 * or clutter. Orthogonal to `OrganizationType` (segment / business model) and to the subscription
 * tier, which stay separate fields. Descriptive only: nothing about access reads it.
 */
export enum OrganizationLabel {
  /** Paying us and/or actively using data. Also active internal organizations and partnerships. */
  ACTIVE = 'Active',
  /** Churned customer or partnership. */
  INACTIVE = 'Inactive',
  /** Prospect: an investor trial set up by hand, or a PLG organization that is not (yet) a customer. */
  TRIAL_ACTIVE = 'Trial active',
  /** Prospect whose trial is over. */
  TRIAL_EXPIRED = 'Trial expired',
  /** Not used anymore; a candidate for deletion. */
  ARCHIVE = 'Archive',
  /**
   * Transitional value the migration gave every organization that existed before labels did. Never
   * offered in the UI and rejected on write: it only disappears, one organization at a time, as the
   * initial labeling pass assigns real values, and its count is that pass's work queue.
   */
  UNLABELED = 'Unlabeled',
}

export const allOrganizationLabels = getAllEnumValues(OrganizationLabel, 'string');

/** The labels a person may set. `UNLABELED` is deliberately absent; see its doc comment. */
export const selectableOrganizationLabels: OrganizationLabel[] = [
  OrganizationLabel.ACTIVE,
  OrganizationLabel.TRIAL_ACTIVE,
  OrganizationLabel.TRIAL_EXPIRED,
  OrganizationLabel.INACTIVE,
  OrganizationLabel.ARCHIVE,
];

export function isOrganizationLabel(value: string): value is OrganizationLabel {
  return allOrganizationLabels.includes(value as OrganizationLabel);
}

export function isSelectableOrganizationLabel(value: string): value is OrganizationLabel {
  return selectableOrganizationLabels.includes(value as OrganizationLabel);
}

/**
 * What set the label. `manual` is a team member on an internal page, `signup` the self-signup flow
 * (always `TRIAL_ACTIVE`), `initial-labeling` the one-off bulk pass over pre-existing organizations.
 * `subscription` and `trial-end` are the automatic paths: a DMA Freemium organization whose
 * subscription becomes active, and a trial whose end date has passed. `first-external-user-joined`
 * and `last-external-user-left` are automatic too: an organization getting its first user outside
 * Upright, or losing its last one (PLT-7484).
 */
export type OrganizationLabelChangeSource =
  | 'manual'
  | 'signup'
  | 'initial-labeling'
  | 'subscription'
  | 'trial-end'
  | 'first-external-user-joined'
  | 'last-external-user-left';

export enum OrganizationType {
  /**
   * @deprecated
   */
  ASSET_MANAGER = 'Asset manager',
  /**
   * @deprecated
   */
  ASSET_OWNER = 'Asset Owner',
  AM_AO = 'AM/AO',
  PE_VC = 'PE/VC',
  /**
   * @deprecated
   */
  SERVICE_PROVIDER = 'Service Provider',
  COMPANY = 'Company',
  /**
   * @deprecated
   */
  PUBLIC_ECOSYSTEM = 'Public organization / ecosystem',
  OTHER = 'Other',
  INTERNAL = 'Internal',
  // Special organization type DMA freemium subscriptions
  DMA_FREEMIUM = 'DMA Freemium',
  // Special organization type for DMA consultant subscriptions
  DMA_CONSULTANT = 'DMA Consultant',
  // Self-serve organizations of the Arvo decision API (docs/development/plans/arvo-console.md)
  ARVO = 'Arvo',
}

export function friendlyOrganizationType(input: OrganizationType | undefined): string | undefined {
  if (input === OrganizationType.OTHER) {
    return undefined;
  } else {
    return input;
  }
}

export const allOrganizationTypes = getAllEnumValues(OrganizationType, 'string');

export const supportedOrganizationTypes: OrganizationType[] = [
  OrganizationType.AM_AO,
  OrganizationType.PE_VC,
  OrganizationType.COMPANY,
  OrganizationType.INTERNAL,
  OrganizationType.DMA_FREEMIUM,
  OrganizationType.DMA_CONSULTANT,
  OrganizationType.ARVO,
  OrganizationType.OTHER,
];

export function isOrganizationType(type: string): boolean {
  return allOrganizationTypes.includes(type as OrganizationType);
}

export interface OrganizationEntityReference {
  id: number;
  entityRef: PossiblyNamedModelEntityReference;
}

// organizations enditin API types:
// The label is required on creation: nothing new may start as `UNLABELED`. The bookkeeping of who
// set it is the server's to fill in.
export type AddOrganizationRequest = Omit<
  Organization,
  'id' | 'createdAt' | 'updatedAt' | 'label' | 'labelChangedAt' | 'labelChangedBy' | 'labelChangeSource'
> & { label: OrganizationLabel };

/**
 * DMA freemiun account creation request
 */
export interface CreateFreemiumAccountRequest {
  companyName: string;
  companyWebsite?: string;
  userEmail: string;
  userFirstName?: string;
  userLastName?: string;
  /** Internal tooling only: also add the created user to the Test users group (id 7). Defaults to false. */
  addToTestUsers?: boolean;
  /** Internal tooling only: also send the created user a platform invite email (HubSpot sync). Defaults to false. */
  sendInviteEmail?: boolean;
  /**
   * Internal tooling only: also create a DEEP variant freeze for the new organization (CSRD delivery).
   * The freeze is created best-effort after the org commits, so it never blocks org creation. Defaults to false.
   */
  createFreeze?: boolean;
  /**
   * Internal tooling only: the label the new organization starts with. Defaults to `Trial active`,
   * which is what a PLG signup means; a team member sets `Active` when creating a paying customer
   * directly. Not on the self-signup request on purpose: a signing-up user never chooses their label.
   */
  label?: OrganizationLabel;
  /** Internal tooling only: the planned end of the trial, as `YYYY-MM-DD`. */
  trialEndsAt?: string;
}

/**
 * DMA freemiun account creation request for an existing user
 */
export interface CreateFreemiumAccountForUserRequest {
  companyName: string;
  companyWebsite?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  organizationType?: OrganizationType.DMA_FREEMIUM | OrganizationType.DMA_CONSULTANT;
}

/**
 *
 */
export interface CreateCompanyForDueDiligenceRequest {
  companyName: string;
  companyWebsite: string;
  // Revenue input in millions, matching the DD wizard UI (e.g. 150 = 150 million).
  // Backend converts this to full currency units before storing revenue datapoints.
  revenue?: number;
  // ISO currency code for the revenue input, e.g. "USD", "EUR".
  // Required whenever revenue is provided.
  revenueCurrency?: string;
  // Reporting year for the revenue input, e.g. 2024
  revenueYear?: number;
  // Total number of employees
  employeeCount?: number;
  // Reporting year for the employee count input, e.g. 2024
  employeeCountYear?: number;
  /** PLT-7194 — the website is one the website lookup found; see `WEBSITE_LOOKUP_SOURCE`. */
  websiteFromLookup?: boolean;
}

/**
 * PLT-7194 — the `websiteURLSource` of a custom company whose website the website lookup found, rather
 * than the user typing it. Where the website came from, nothing more: nothing asks the user to confirm
 * it (PLT-7195), and changing the website stores the source `manual`, as for a typed one.
 */
export const WEBSITE_LOOKUP_SOURCE = 'website-lookup';

export interface CreateCompanyForDueDiligenceResponse {
  companyId: string;
  gptJobId?: number;
  /**
   * PLT-6290 — the generation trigger the server chose for this company. The wizard must
   * publish and poll under the same one: both operations are filtered by trigger, so guessing
   * wrong finds no job and the wizard waits forever on a creation that in fact succeeded.
   *
   * Reported rather than assumed by the client even though this endpoint chooses `INVESTOR_DD`
   * for every company today, because the choice is the server's to make and the supplier
   * creation path (PLT-6267) will choose differently.
   *
   * Optional for the same reason `GetJobStatusByCompanyResponse.trigger` is: absent on a
   * response served by an older backend — a new UI bundle can POST to a task still running the
   * previous handler mid-rolling-deploy — so callers still need a fallback. The wizard's
   * `createCompanyMutation.data?.trigger ?? …` is that fallback, and marking this required
   * would make it read as redundant while the runtime window is real.
   */
  trigger?: GPTProductSetGenerationTrigger;
}

/**
 * PLT-5509 — one existing company the organization already has access to that
 * matches the name/website the user is about to create. `accessTier` decides
 * the wording: `same-organization` = "you already created this";
 * `accessible-elsewhere` = "Upright already covers this, you can just view it".
 * `isStrong` is true for website / identifier matches (the ones the wizard
 * warns on); name-only matches are informational.
 */
export interface ExistingCompanyMatchDto {
  companyId: string;
  companyName: string;
  /** How the match was made (e.g. "website", "ISIN") — for honest display. */
  matchBasis: string;
  accessTier: 'same-organization' | 'accessible-elsewhere';
  /** True when the current user created this company — shown as "your custom company". */
  createdByCurrentUser: boolean;
  isStrong: boolean;
  /** Relative deep link to the existing company, e.g. `/company/{id}`. */
  deepLinkUrl: string;
}

/**
 * PLT-5509 — response of the pre-create existing-company check. `confirmMatches`
 * is the subset the wizard actually warns on; `matches` carries everything for
 * completeness. The check never blocks creation — the user can always proceed —
 * with one exception on the supplier route, `ownCompanyMatch`.
 */
export interface CheckExistingCompanyResponse {
  matches: ExistingCompanyMatchDto[];
  strongMatches: ExistingCompanyMatchDto[];
  /**
   * PLT-6982 — what the wizard warns on. On the investor route: the strong matches, plus a name
   * match on one of the organization's own custom companies. On the supplier route: the strong
   * matches only.
   */
  confirmMatches: ExistingCompanyMatchDto[];
  hasStrongMatch: boolean;
  hasSameOrganizationMatch: boolean;
  /**
   * PLT-6268 — set by the supplier check when what the user is typing matches the buyer itself.
   * Never in `matches`: it is not a duplicate supplier, and the wizard answers it differently — a
   * block when the match is strong (a company cannot be its own supplier), a warning when it is
   * name-only. Absent on the investor route, which has no buyer.
   */
  ownCompanyMatch?: OwnCompanyMatchDto;
}

/**
 * PLT-7057 — find the official website of a company the user is about to create, when they do not
 * know it. One lookup behind the investor and supplier wizards and the chat tool alike.
 */
export interface CompanyWebsiteLookupRequest {
  companyName: string;
  /** ISO 3166-1 alpha-2 or alpha-3. Tells same-named companies apart. */
  country?: string;
  /** What the company does, in the user's words. Tells same-named companies apart. */
  description?: string;
}

/**
 * `found`: exactly one high-confidence candidate, listed first (any others are lower-confidence
 * alternatives). `ambiguous`: the user has to pick. `not-found`: nothing to offer.
 * `unavailable`: the lookup could not search right now — says nothing about whether the company
 * has a website, so the copy has to differ from `not-found`.
 */
export type CompanyWebsiteLookupOutcome = 'found' | 'ambiguous' | 'not-found' | 'unavailable';

export interface CompanyWebsiteCandidate {
  /** The homepage, normalized, and already through the website validity check. */
  website: string;
  /** The organisation the website belongs to — what tells same-named candidates apart. */
  companyName: string;
  /** ISO 3166-1 alpha-2, when known. */
  country?: string;
  confidence: 'high' | 'medium';
  /**
   * `upright`: a company in Upright's own database the organization can already open, with the
   * website stored for it. `web`: found by the web search.
   */
  source: 'upright' | 'web';
  /** One sentence on who the company is and why the site is theirs; web candidates only. */
  reason?: string;
  /** Pages the web search found the site on — the evidence. Empty for `upright` candidates. */
  sourceUrls: string[];
}

export interface CompanyWebsiteLookupResponse {
  outcome: CompanyWebsiteLookupOutcome;
  /** Best first. Empty unless `outcome` is `found` or `ambiguous`. */
  candidates: CompanyWebsiteCandidate[];
}

/**
 * PLT-7086 — correct the website of a custom company created from the wrong one (usually a lookup
 * answer the user rejects). Before its first publish this reruns product-set generation and site
 * identification on the new website; after it (PLT-6278, the Edit company dialog) the website is
 * only stored.
 */
export interface ChangeCompanyWebsiteRequest {
  companyWebsite: string;
}

export interface ChangeCompanyWebsiteResponse {
  companyId: string;
  /** The website as stored: normalized, the same form creation stores. */
  websiteURL: string;
  /**
   * `rerun` before the first publish, `stored` after it or for a company built from files (the website
   * changed and nothing reran), and `unchanged` when the website already was this one.
   */
  outcome: 'rerun' | 'stored' | 'unchanged';
  /** The new generation job, present exactly when `outcome` is `rerun`. */
  gptJobId?: number;
  /**
   * PLT-7127: the company's products were built from files, so only the address was stored. Its
   * products and description are kept and nothing reran.
   */
  productsKept?: true;
  /**
   * PLT-7302: a company built from files kept the sites the user saved while it had no website, so
   * none were identified from the new one.
   */
  sitesKept?: true;
}

/**
 * PLT-6285 — identify a published custom company's products, sites or both again, from the website
 * the user changed it to in the Edit company dialog. What is identified replaces the drafts and waits
 * for Save and recalculate, like the dialog's other edits.
 */
export interface ReidentifyCompanyRequest {
  products: boolean;
  sites: boolean;
}

export interface ReidentifyCompanyResponse {
  companyId: string;
  /** The generation job, present exactly when products were asked for. */
  gptJobId?: number;
  /** The site-identification job, present exactly when sites were asked for. */
  siteJobId?: number;
}

/**
 * How a run identifying the products or sites again stands:
 *
 * - `running`: still identifying. What it finds replaces the drafts when it finishes, so the dialog
 *   holds its edits until then.
 * - `saved`: finished, and what it identified is the company's draft.
 * - `failed`: finished without a draft; the inputs are as they were.
 */
export type ReidentifyRunState = 'running' | 'saved' | 'failed';

/**
 * PLT-6285 — `GET /api/company/{companyId}/reidentify-status`. Each is null when the company's latest
 * run of that kind is not one identifying it again.
 */
export interface GetCompanyReidentifyStatusResponse {
  products: { jobId: number; state: ReidentifyRunState } | null;
  sites: { jobId: number; state: ReidentifyRunState } | null;
  /**
   * How many more times the company can be identified from a new website, a website change before
   * the first publish included; one waiting for a recalculation already holds its place.
   */
  identificationsLeft: number;
}

/**
 * PLT-6278 — `PATCH /api/organizations/{organizationId}/suppliers/{companyId}`: the buyer's own facts
 * about a supplier, changed from the Edit company dialog. A field left out is kept; `null` clears it.
 * Neither is an input to the assessment, so a change applies at once and needs no recalculation.
 */
export interface UpdateSupplierDetailsRequest {
  internalId?: string | null;
  /** In the buyer's spend currency (`GetSuppliersResponse.spendCurrency`). */
  spendAmount?: number | null;
}

export interface UpdateSupplierDetailsResponse {
  companyId: string;
  internalId: string | null;
  spendAmount: number | null;
}

/**
 * PLT-6267 — create a supplier assessment through the wizard.
 *
 * A separate request type from `CreateCompanyForDueDiligenceRequest`, and a separate endpoint,
 * because the intent has to be in the request: an entitlement cannot say which flow the user is
 * in, and every organization that may assess suppliers also holds `CREATE_COMPANIES`, so deriving
 * the supplier label from a privilege would stamp it on ordinary custom companies too.
 */
export interface CreateSupplierRequest {
  companyName: string;
  /**
   * Required, as for investor DD (PLT-7059): the product set is generated from it. The wizard looks
   * one up for a user who does not know it (`/suppliers/website-lookup`) before posting here.
   */
  companyWebsite: string;
  /**
   * The company this supplier is assessed FOR. Omit it and the server resolves it — the
   * organization's own company, or the single client company the caller can reach. When it cannot,
   * the response is a 400 carrying `errorCode: "SUPPLIER_BUYER_COMPANY_REQUIRED"` and the wizard
   * asks the user which client.
   */
  buyerCompanyId?: string;
  revenue?: number;
  revenueCurrency?: string;
  revenueYear?: number;
  employeeCount?: number;
  employeeCountYear?: number;
  /**
   * PLT-6970 — the buyer's own ID for this supplier, such as its vendor number. Stored as the
   * supplier company's EID in the buyer's audience user group, so an ID already given to another
   * company in that group is refused with 409 and `errorCode: "SUPPLIER_INTERNAL_ID_TAKEN"`.
   */
  internalId?: string;
  /** PLT-6970 — the buyer's annual spend with this supplier, in the buyer's spend currency. */
  spendAmount?: number;
  /** PLT-7194 — the website is one the website lookup found; see `WEBSITE_LOOKUP_SOURCE`. */
  websiteFromLookup?: boolean;
  /**
   * PLT-7201 — stop for the user to review the products and site locations before the assessment
   * runs. Left out, the supplier runs straight through: its products and sites are accepted as
   * identified and the assessment is computed, as a supplier import does.
   */
  reviewBeforeCompute?: boolean;
}

/**
 * PLT-6970 — one buyer's supplier settings: today, the currency every supplier's spend is entered
 * and shown in. EUR until the buyer changes it.
 */
export interface SupplierSettings {
  buyerCompanyId: string;
  spendCurrency: string;
}

/**
 * PLT-6970 — change a buyer's spend currency. Relabels the amounts already entered; never converts
 * them.
 */
export interface UpdateSupplierSettingsRequest {
  spendCurrency: string;
}

/**
 * PLT-6970 — whether an internal ID is free to give a new supplier of this buyer. Asked by the Add
 * supplier form when the field is left, because the wizard cannot show why a creation failed
 * (PLT-6490): a clash found only by the create route would reach the user as a blank "Failed to
 * create company".
 */
export interface SupplierInternalIdCheckResponse {
  internalId: string;
  /**
   * The supplier of this buyer that already has the ID, or null when it is free. A clash with a
   * company that is not one of this buyer's suppliers is reported without a name — `takenBy` is
   * `{ companyId: null, companyName: null }` — so the check cannot be used to learn what else the
   * group holds.
   */
  takenBy: { companyId: string | null; companyName: string | null } | null;
}

/**
 * Where a supplier is in its assessment, for the Suppliers list (PLT-6267).
 *
 * Read off the **newest job** for the company and decoded by that job's type: a
 * `generate-gpt-product-set` job describes modelling the company, a `publish-ipr-product-set` job
 * describes assessing it. Taking the newest rather than a fixed one is also what makes
 * re-generating after an assessment read correctly.
 *
 * **When no job row survives, the product sets decide** — successful jobs are deleted after three
 * days, so that is the normal state of any supplier assessed last week, not an edge case.
 * `supplier-status.ts` owns the ordering; the members below say what each value means rather than
 * which source produced it.
 *
 * **Not `DdLifecycleStatus`, and deliberately so** (Roni, 2026-09-10). The chat card's five states
 * collapse both failures into one `error`, and a supplier needs them apart: "we could not model
 * this company" and "we modelled it but could not assess it" send the reader to different places.
 * Both types still read the same job rows, so they cannot disagree about *what happened* — only
 * about how much detail they report.
 */
export type SupplierAssessmentStatus =
  /**
   * Nothing exists yet: no results, and no products entered. The state a supplier created without
   * a usable website sits in until someone describes what it sells, because generation was skipped
   * on purpose and no job is coming.
   *
   * Every other status would be a lie about it, and the closest one, `modelling`, is the exact
   * forever-spinner PLT-6491 is about — "no job row" and "job not queued yet" are
   * indistinguishable to a reader who has only the running states.
   */
  | 'not-modelled'
  /** Product-set generation is running. */
  | 'modelling'
  /**
   * Products exist and are waiting for the user to review and publish them — whether generation
   * produced them or the user typed them in by hand, which is the website-less path and has no job
   * behind it.
   */
  | 'awaiting-review'
  /** Publish is running: impacts, risks and opportunities are being computed. */
  | 'assessing'
  /**
   * Results exist. Evidenced by a successful publish job, or — once that job has been pruned — by
   * the published product set it left behind, which is the durable record.
   */
  | 'assessed'
  /** Generation failed: there is no model of the company to assess. */
  | 'modelling-failed'
  /** The company was modelled but the assessment itself failed. */
  | 'assessment-failed';

export interface SupplierListItem {
  companyId: string;
  companyName: string;
  /** Null for a supplier with no web presence, which is a supported state, not missing data. */
  websiteURL: string | null;
  status: SupplierAssessmentStatus;
  /**
   * Products in the supplier's newest published product set — leaves only, segments excluded — or
   * null when it has published none.
   *
   * The null is load-bearing. A published set holding no products is a finding worth showing as
   * `0`; a supplier that has published nothing has no answer at all, and rendering both as zero
   * would hide the first behind the second.
   */
  productCount: number | null;
  /**
   * Geocoded sites on the supplier's newest published geolocation row, or null when no row has been
   * published yet.
   *
   * Sites are identified inside the publish job, so a supplier that has not been assessed has no
   * count rather than a count of zero. Only sites carrying coordinates are counted: those are the
   * ones the climate scorer can resolve, so a larger number here would promise cover that does not
   * exist.
   */
  siteLocationCount: number | null;
  /**
   * When the supplier's results were last computed — `publishedAt` of its newest published product
   * set — or null when it has published none (PLT-6269).
   *
   * The results panel's header says "Assessed <date>", so the reader can tell a fresh read from one
   * made before they last edited the supplier's inputs. It rides on the list row rather than on the
   * risk read because the header renders the moment the panel opens, before the bands arrive.
   *
   * **Read off the publish, not off the job.** `_cleanupFinishedJobs` deletes every successful job
   * after three days, so a job timestamp would blank out on exactly the suppliers that have been
   * assessed longest — the same trap `resolveSupplierStatus` documents its fallback for. This is the
   * same set `productCount` counts, so the two columns cannot describe different publishes.
   *
   * Null is a real state and not a gap: a supplier mid-generation has published nothing to date.
   */
  assessedAt: string | null;
  /** PLT-6970 — the buyer's own ID for this supplier (its EID in the buyer's audience group), or null. */
  internalId: string | null;
  /**
   * PLT-6970 — the buyer's annual spend with this supplier, in the response's `spendCurrency`, or
   * null when nobody has entered one. Null is not zero: it is left out of every share of spend.
   */
  spendAmount: number | null;
  /**
   * Whether **this caller** may delete the supplier — the answer `DELETE …/suppliers/{companyId}`
   * would give, computed up front so the list never offers a control the route then refuses.
   *
   * Per row rather than once per response because access is granted per company: as the grants
   * stand today every row of one list answers alike, but the list reads the access table rather
   * than restating the rule that populates it. The page renders the bin only where this is true;
   * the server re-checks on the delete itself regardless.
   */
  canDelete: boolean;
}

/**
 * PLT-6267 — everything the Suppliers page needs to know before it can ask anything else.
 *
 * Company-scoped, because the DMA dashboard is: it shows one company, and that company is both
 * the buyer and the thing that identifies the organization. Asking in one call is what stops the
 * nav row and the page disagreeing about whether the feature is available.
 *
 * **Why not `GET /company/{id}/organization`.** That endpoint matches on `organization.companyId`,
 * which is the organization's *own* company — and a DMA Consultant organization has none, its
 * clients hang off `entity_organization_relation`. So it answers 410 for every consultancy client,
 * which is exactly the population this page is for. That is the R4 shape again: `companyId` on the
 * organization answers "is this its own company", never "which organization is this company in".
 * The older endpoint keeps its meaning — four shipped surfaces depend on it — and this one asks
 * the supplier domain's own question.
 */
export interface GetCompanySupplierAccessResponse {
  /** The organization this company belongs to, or null when it belongs to none. */
  organizationId: number | null;
  /**
   * Whether **this caller** may assess suppliers for that organization. Per caller by design, so
   * it decides what to *show*; the server re-checks on every write.
   */
  canAssessSuppliers: boolean;
  /**
   * Whether this caller can also add and assess this company's suppliers in chat: the above, the
   * chat's supplier tools are available to them, and they may edit this company, which adding a
   * supplier to it takes (PLT-7204). What the DMA home's "Assess a supplier" card is shown on
   * (PLT-7200), so it never opens a chat that cannot answer it.
   */
  canAssessSuppliersInChat: boolean;
  /**
   * Whether **this buyer company** has custom company climate risk switched on (PLT-6994): on by
   * default for a buyer in a DMA Freemium or DMA Consultant organization (PLT-7200), otherwise a
   * user group linked to it holds `USE_CUSTOM_COMPANY_CLIMATE_RISK`. What the Suppliers list's Site
   * locations column is shown on, and the same answer a customer user's supplier publishes
   * identify sites on (PLT-7037), so the column never counts sites that were never looked for.
   * Team users' supplier publishes identify sites regardless.
   *
   * Per company, not per caller, unlike `canAssessSuppliers`: no team-user shortcut, so a team
   * user looking at a customer's suppliers sees what that customer sees.
   */
  hasCustomCompanyClimateRisk: boolean;
}

/**
 * PLT-6267 — the Suppliers list, scoped to ONE buyer.
 *
 * Never organization-wide. A consultancy's clients must not learn about each other, and the buyer
 * is echoed back so the page can prove which client it is showing rather than inferring it.
 */
export interface GetSuppliersResponse {
  buyerCompanyId: string;
  suppliers: SupplierListItem[];
  /**
   * PLT-6970 — the buyer's spend currency: what every `spendAmount` on the list is in, and what the
   * Add supplier form asks the spend in. On the list, like `allowance`, so the column header and the
   * amounts under it come from one read and cannot disagree.
   */
  spendCurrency: string;
  /**
   * How many suppliers this buyer has created and may still create (PLT-6268).
   *
   * Carried on the list rather than served by a route of its own: every usage-limits route is
   * `/api/admin/…` behind a team-or-admin token, the figures are per buyer exactly as the list is,
   * and the page already fetches this response — so the "2 of 3 suppliers used" line and the rows
   * it counts come from one read and cannot disagree.
   */
  allowance: SupplierAllowance;
  /**
   * Whether this caller may hand a supplier to the agent — the results panel's "Continue in chat"
   * (PLT-6269, D26).
   *
   * The server's answer and not a privilege the page re-derives (`canContinueSupplierInChat`): the
   * caller has `get_supplier_risk_assessment`, the tool the agent would answer the question with,
   * and may use web chat at all (`USE_AI_CHAT_WEB`). So the button follows the tool's audience
   * with no frontend edit, and is never offered to a reader the chat would refuse (PLT-6581).
   *
   * On the list rather than on the risk route, for the same reason `allowance` is here: the panel's
   * footer renders before its body has arrived, and a flag riding on the assessment read would let
   * the primary action pop in a beat late.
   */
  canContinueInChat: boolean;
}

/**
 * One supplier's overall risk band — the Suppliers list's Overall risk column (PLT-6659).
 */
export interface SupplierRiskLevelItem {
  companyId: string;
  /**
   * The band the results panel shows as its headline, or null when this caller has none to read:
   * the supplier has no assessment yet, or the findings behind its score were withheld from them.
   *
   * Null is "unknown", never "quiet". A supplier that cannot be scored must not read as a
   * low-risk one, so the column renders this as an em dash and the sort puts it after Very low —
   * the same rule the refusal envelope enforces on the panel and the chat card.
   */
  level: SupplierRiskLevel | null;
}

/**
 * PLT-6659 — the bands behind the Suppliers list's Overall risk column, for one buyer.
 *
 * **A route of its own, beside the list rather than a field on it**, for the reason PLT-6269
 * already recorded for the panel's read: the list is re-asked every ten seconds while any supplier
 * is mid-flight, and banding a supplier costs a DMA summary and a findings read. Folding these in
 * would pay for every row's bands every ten seconds; asked separately, they are read when a
 * supplier's status or assessment date actually changes.
 *
 * Buyer-scoped exactly like the list, and the buyer is echoed back for the same reason.
 */
export interface GetSupplierRiskLevelsResponse {
  buyerCompanyId: string;
  levels: SupplierRiskLevelItem[];
}

/**
 * PLT-7184 — one supplier's levels on the Supplier base page, as this caller may see them.
 *
 * - A snapshot, assessed or withheld: the levels, or why there are none for this supplier.
 * - `unavailable`: nothing this caller may be shown — no assessment, no access, or a read that
 *   failed. Unknown, never quiet, exactly like a null band on the Suppliers list.
 * - `pending`: not read yet. The request stopped starting reads at its deadline; asking again picks
 *   up where it stopped.
 */
export type SupplierBaseRisk = SupplierRiskSnapshot | { outcome: 'unavailable' } | { outcome: 'pending' };

/**
 * A product the supplier sells, from its newest published product set, with where it sits in the
 * taxonomy: the three levels of the page's product filter, broadest first `sectors`, then
 * `productCategory`, then `productGroup`.
 */
export interface SupplierBaseProduct {
  title: string;
  /**
   * PLT-7811 — the taxonomy product it is mapped to, by code: what the Upstream map traces it by
   * (`UpstreamTrace.productCode`). Null where `productGroup` is, so a product the taxonomy does not
   * know is unmapped here too.
   */
  productCode: string | null;
  /** The taxonomy product it is mapped to, by title. */
  productGroup: string | null;
  /**
   * PLT-7260 — the taxonomy product `productGroup` is built on, by title: an IPRA mixin's base
   * product ("ABS" is built on "Acrylonitrile butadiene styrene"), or the mapped product itself when
   * it is not a mixin. Null when the product is not mapped.
   */
  productCategory: string | null;
  /**
   * PLT-7260 — the taxonomy's industry sectors `productCategory` sits under, named without the
   * taxonomy's " sector" suffix ("Rubber and plastics products"), sorted. Nearly always one; empty
   * where the taxonomy places the product under no sector.
   */
  sectors: string[];
}

/** PLT-7260 — a country the supplier operates in, and its own sites there. */
export interface SupplierBaseCountry {
  /** The country's name, or the site's country as entered where it is not one we recognise. */
  name: string;
  /** ISO 3166-1 alpha-3, or null where the entered country is not one we recognise. */
  code: string | null;
  /** The supplier's own sites in it, by the snapshot's `siteId`. */
  siteIds: number[];
}

export interface SupplierBaseItem extends Omit<SupplierListItem, 'canDelete'> {
  risk: SupplierBaseRisk;
  /**
   * Leaves only, as `productCount` counts them. Empty unless `risk` is assessed: the products are
   * the read's inputs, and a caller who may not see the read is not shown them either.
   */
  products: SupplierBaseProduct[];
  /**
   * Countries of the supplier's own operations among the assessed sites, each once, by name. Empty
   * unless assessed.
   */
  operatingCountries: SupplierBaseCountry[];
  /**
   * The buyer's written line per topic, keyed by the snapshot's topic name, or null when there is no
   * current write-up beside these levels. The page then words each topic from its level.
   */
  topicLines: Record<string, string> | null;
}

/**
 * PLT-7184 — the Supplier base page: every supplier of ONE buyer with its levels, in one read.
 *
 * Buyer-scoped like the Suppliers list. The page asks again while `pendingCount` is above zero.
 */
export interface GetSupplierBaseRiskResponse {
  buyerCompanyId: string;
  /** PLT-7737 — the buyer's name, for the chat this page opens; null where it could not be read. */
  buyerCompanyName: string | null;
  spendCurrency: string;
  /**
   * PLT-7186 — in the order the page lists them (`orderSupplierBase`): assessed first, riskiest overall
   * level first and the larger spend first among equals, then the rest in the list's order. The page
   * keeps this order rather than sorting its own.
   */
  suppliers: SupplierBaseItem[];
  /** Suppliers whose `risk` is `pending`. */
  pendingCount: number;
  /**
   * PLT-7186 — whether the results panel this page opens offers "Continue in chat": the same rule
   * as `GetSuppliersResponse.canContinueInChat`, so the panel is gated alike on both pages.
   */
  canContinueInChat: boolean;
  /**
   * PLT-7737 — whether the page itself offers "Continue in chat": the same rule for the tool that
   * answers about the whole base, `get_supplier_base_report`.
   */
  canContinueBaseInChat: boolean;
}

export interface CreateSupplierResponse {
  companyId: string;
  /** The buyer the supplier was filed under, so the wizard can route back to the right list. */
  buyerCompanyId: string;
  /**
   * The generation job queued for the supplier's website. Absent only when queuing failed, which
   * the trigger logs rather than throws.
   */
  gptJobId?: number;
  /**
   * The generation trigger the server chose. Reported for the same reason as on the investor-DD
   * response: publish and poll are both filtered by trigger, so a client that guesses finds no job
   * and waits forever on a creation that succeeded.
   */
  trigger?: GPTProductSetGenerationTrigger;
}

/**
 * Result of the optional best-effort variant-freeze creation that accompanies org creation.
 * The freeze is created outside the org transaction, so the org always succeeds regardless of
 * this outcome. On success the new freeze id is returned (surfaced in copyable form in the UI);
 * on failure a flag is returned so the UI can prompt manual creation.
 */
export type FreezeCreationResult = { status: 'created'; freezeId: string } | { status: 'failed' };

/**
 * Success response for freemium account creation
 */
export interface CreateFreemiumAccountResponse {
  organizationId: number;
  userGroupId: number;
  companyId: string;
  userId: number;
  gptJobId?: number;
  /** Present only when `createFreeze` was requested; reports the outcome of the freeze creation. */
  freeze?: FreezeCreationResult;
}

// Self-signup can create either a corporate (has a company) or consultant (no company) account.
export type CreateFreemiumAccountForUserResponse = Omit<CreateFreemiumAccountResponse, 'companyId'> & {
  companyId: string | null;
};

// NOTE: if provided, ssoProperties will replace the existing one, so in addition to the edited fields of ssoProperties, all the unedited fields should also be present in the payload
export type EditOrganizationRequest = Partial<
  Pick<Organization, 'description' | 'companyId' | 'ssoProperties' | 'name' | 'type' | 'label' | 'trialEndsAt'>
>;

export interface AddUserGroupsToOrganizationRequest {
  organizationId: number;
  userGroupId: number;
}

export interface AddEntitiesToOrganizationRequest {
  organizationId: number;
  entityRefs: ModelEntityReference[];
}
export interface RemoveEntitiesFromOrganizationRequest {
  entityRelationIds: number[];
}

export interface GetOrganizationAccessResponse {
  organizationId: number;
  role: Role;
  canCreateCompanies: boolean;
  /**
   * Whether **this caller** may assess suppliers for this organization (PLT-5812): the
   * organization is a PLG one (DMA Freemium or DMA Consultant) *and* the caller holds
   * `ASSESS_SUPPLIERS` on it. This flag and the handler that lists an organization's custom
   * companies are computed from the same predicate (`canAssessSuppliers`), so the capability
   * advertised here and the guard that enforces it cannot drift. The supplier endpoints will
   * assert the same predicate through `assertCanAssessSuppliers`.
   *
   * Per caller, not per organization — a team user reads `true` on any PLG organization via
   * the privilege system's team short-circuit, and for a customer it depends on the groups
   * their user inherits. Do not read this field to decide whether an *organization* is
   * eligible.
   *
   * It does not answer "for which client": in a DMA Consultant organization the privilege is
   * inherited by every client-level user beneath the org-level group, so a supplier's own
   * relation is what scopes it to one client. That scoping has to be applied by each consumer;
   * this flag does not carry it.
   */
  canAssessSuppliers: boolean;
  /**
   * Whether **this organization** has custom company climate risk switched on (PLT-6971): its
   * root user group, or an organization-level group directly under it, holds
   * `USE_CUSTOM_COMPANY_CLIMATE_RISK`. That privilege is what makes the publish job identify a
   * custom company's sites, so this is what the Custom companies list's Site locations column is
   * shown on.
   *
   * Per organization, not per caller, unlike the flags above: no team-user shortcut, so a team
   * user looking at a customer's organization sees what that customer sees.
   */
  hasCustomCompanyClimateRisk: boolean;
}

export type GetOrganizationsResponse = Array<Organization & { company: { id: string; name: string } | null }>;
export type GetOrganizationResponse = Organization & { company: { id: string; name: string } | null };
export type GetOrganizationCountsByTypeResponse = {
  total: number;
  byType: Partial<Record<OrganizationType, number>>;
};
export type GetOrganizationCountsByLabelResponse = {
  total: number;
  byLabel: Partial<Record<OrganizationLabel, number>>;
};
export type AddOrganizationResponse = Organization;
export type EditOrganizationResponse = Organization;
export type AddUserGroupsToOrganizationResponse = Organization;
export type AddEntitiesToOrganizationResponse = OrganizationEntityReference[];

/**
 * Payment status for a company in the accessible companies list.
 * - 'not_required': First company (free), no payment needed
 * - 'pending': Payment required but not yet completed
 * - 'completed': Payment completed, access granted
 * - 'expired': Payment was completed but access has expired (12 months passed)
 */
export type CompanyPaymentStatus = 'not_required' | 'pending' | 'completed' | 'expired';

/**
 * Resolved subscription tier for a paid company in a DMA Consultant org.
 * Only set when `paymentStatus === 'completed'` — for free, pending, or expired companies
 * the effective tier is 'starter' (or the org-level subscription, depending on context).
 * 'enterprise' is granted only via admin override; there is no Stripe self-serve path for it.
 */
export type CompanyPaymentTier = 'pro' | 'enterprise';

export interface AccessibleCompanySummary {
  companyId: string;
  companyName?: string;
  accessLevel: AccessLevel;
  /**
   * Payment status for this company.
   * Only relevant for DMA Consultant organizations where 2nd+ companies require payment.
   * For other organization types, this will be 'not_required'.
   */
  paymentStatus?: CompanyPaymentStatus;
  /**
   * Resolved subscription tier for this company. Only set when `paymentStatus === 'completed'`.
   * Lets the lander render the right pill (PRO vs. ENTERPRISE) without an N+1 lookup per row.
   */
  paymentTier?: CompanyPaymentTier;
  /**
   * Whether the current user is a company-level admin for this company.
   * Only relevant for DMA Consultant organizations where company-level admin groups exist.
   */
  isCompanyAdmin?: boolean;
  /**
   * Whether the current user has pinned this company to the top of their
   * consultant dashboard (PLT-5113). Per-user; populated from `user_company_pin`.
   * Affects dashboard ordering only — not the company-header dropdown or chat.
   */
  isPinned?: boolean;
}

export interface GetMyAccessibleCompaniesResponse {
  organization: Organization;
  companies: AccessibleCompanySummary[];
}

// Organization content getter APIs:
export interface GetMyOrganizationsResponse {
  organizations: Organization[];
  hasAccessiblePortfolios: boolean;
  /**
   * Team users only: the organization their selector opens on, or `null` for Upright. Set at
   * `/internal/team-default-organization`.
   */
  teamDefaultOrganizationId?: number | null;
}
export type GetMyOrganizationsEntitiesResponse = OrganizationEntityReference[];

export type GetOrganizationOfCompanyResponse = Pick<Organization, 'id' | 'type' | 'name' | 'companyId' | 'userGroup'>;

// PLT-5113: pin/unpin a company on the consultant dashboard (per-user).
// Both the company and org are taken from the route path, so the bodies are empty.
export type PinCompanyResponse = { success: true };
export type UnpinCompanyResponse = { success: true };

export type AddMyFollowedEntityRequest = ModelEntityReference;
export type RemoveMyFollowedEntityRequest = ModelEntityReference;
export type GetMyFollowedEntitiesResponse = ModelEntityReference[];
export type GetMyFollowedEntityInfosResponse = EntityInfo[];

export type OrganizationAssetInfo = Omit<EntityInfo, 'modelResult'> & {
  groupType?: MeasurableGroupType;
  modelResult: Pick<EntityInfo['modelResult'], 'resultId' | 'netScoresByType' | 'impactScoresByTIVV'>;
};
export type GetOrganizationsAssetsResponse = OrganizationAssetInfo[];

export type GetOrganizationCreatedCompaniesResponse = DueDiligenceCompanyInfo[];

export type GetOrganizationHasCompaniesResponse = {
  hasCompanies: boolean;
};

/**
 * PLT-6276 — the investor organization a custom company belongs to, for the company page's Edit
 * company button. Null when the company is not an investor's custom company (a supplier, a DMA
 * organization's own company or client, an ordinary company), or the caller may not edit it.
 */
export interface CustomCompanyOrganizationResponse {
  organizationId: number | null;
}

type Breakdown = {
  portfolios?: MyPortfolioInfo[];
  // Funds are not supported for performance reasons in this endpoint.
  //  funds?: MyFundInfo[];
  companies?: MyCompanyInfo[];
  genericCompanyGroups?: MyCompanyGroupInfo[];
};

export type GetOrganizationsAssetsBreakdownResponse = Breakdown;

export type GetAccessiblePortfoliosResponse = MyPortfolioInfo[];
export type GetAccessiblePortfolioContentsBreakdownResponse = Breakdown;

export interface OrganizationWithCompanyId extends Organization {
  companyId: string;
}
export function isOrganizationWithCompany(organization: Organization): organization is OrganizationWithCompanyId {
  return !!organization.companyId;
}

// Single sign-on specific properties. Expand this if we bring other SSO
// providers, or if we decide to go deeper with the integration of a single
// provider, e.g. by enabling accesses based on customer-controlled user groups.
export type OrganizationSsoProperties = {
  microsoftTenantId?: string | null;
  googleCloudOrganizationDomain?: string | null;
};

/**
 * Request body for converting a DMA Freemium organization to DMA Consultant.
 * All fields are optional.
 */
export interface ConvertFreemiumToConsultantRequest {
  // Optional new name for the organization (e.g. consultancy name instead of company name)
  newOrganizationName?: string;
}

/**
 * Response for the DMA Freemium → DMA Consultant conversion endpoint.
 */
export interface ConvertFreemiumToConsultantResponse {
  organizationId: number;
  previousType: OrganizationType.DMA_FREEMIUM;
  newType: OrganizationType.DMA_CONSULTANT;
  memberUserGroupId: number;
  companyUserGroupId: number;
  usersConverted: number;
}
